import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Mail, Phone } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { errorMessage } from './errors';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { mergeQueue, CONTACT_TYPES, LEAD_STATUSES } from './unifiedContactsApi';
import { formatPhone } from './shared/phone';
import GhlBadge from './board/GhlBadge';
import { useAuth } from './AuthContext';
import { formatDate } from './kit/time';

// GHL merge queue review (ticket 122 S6) — sync.py never auto-merges a
// GHL-matched pair, it queues it here for a `contacts.merge` staff member.

const KIND_LABELS = {
  ghl_vs_bfy: 'GHL ↔ BFY',
  ghl_duplicate: 'GHL duplicates',
  detached: 'Detached',
};
const REASON_LABELS = {
  email_match: 'Same email',
  phone_match: 'Same phone',
  ghl_id_gone: 'GHL record removed',
};
const STATUS_PILL = {
  merged: { label: 'Merged', bg: '#DCFCE7', color: '#166534' },
  dismissed: { label: 'Kept separate', bg: '#E2E8F0', color: '#475569' },
  superseded: { label: 'Superseded', bg: '#E2E8F0', color: '#475569' },
  archived: { label: 'Archived', bg: '#E2E8F0', color: '#475569' },
};
const KIND_FILTERS = [
  { k: '', l: 'All' },
  { k: 'ghl_vs_bfy', l: 'GHL ↔ BFY' },
  { k: 'ghl_duplicate', l: 'GHL duplicates' },
  { k: 'detached', l: 'Detached' },
];

const PAGE_SIZE = 20;

const dateStr = (iso) => (iso ? formatDate(iso) : '—');
const label = (arr, k) => (arr.find((x) => x.k === k)?.l) || k;

function MergeQueueItem({ item, survivorId, onChooseSurvivor, busy, onMerge, onDismiss, onArchive }) {
  const resolved = item.status !== 'open';
  const pillKey = item.status === 'dismissed' && ['superseded', 'archived'].includes(item.resolution) ? item.resolution : item.status;
  const pill = STATUS_PILL[pillKey] || { label: item.status, bg: '#E2E8F0', color: '#475569' };
  const isPair = (item.contact_ids || []).length === 2;
  const mergeAllowedForKind = item.kind !== 'ghl_duplicate';
  const detached = item.kind === 'detached';

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid={`mq-item-${item.item_id}`}>
      <div className="flex items-center gap-3 mb-3">
        <span className="text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
          {KIND_LABELS[item.kind] || item.kind}
        </span>
        <span className="text-sm text-slate-500">{REASON_LABELS[item.reason] || item.reason}</span>
        <span className="text-xs text-slate-400 ml-auto">{dateStr(item.created_at)}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {(item.contacts || []).map((c) => (
          <div key={c.contact_id} className="border border-slate-100 rounded-xl p-3">
            {!resolved && isPair && mergeAllowedForKind && (
              <label className="flex items-center gap-2 mb-2 text-xs text-slate-500">
                <input
                  type="radio"
                  name={`mq-survivor-${item.item_id}`}
                  checked={survivorId === c.contact_id}
                  onChange={() => onChooseSurvivor(c.contact_id)}
                  data-testid={`mq-keep-${item.item_id}-${c.contact_id}`}
                />
                Keep this record
              </label>
            )}
            <div className="flex items-center gap-2">
              <Link to={`/portal/contacts/${c.contact_id}`} className="font-semibold text-sm" style={{ color: '#070B5F' }}>
                {c.display_name}
              </Link>
              {c.ghl_managed && <GhlBadge />}
              {detached && c.contact_id === item.detached_contact_id && (
                <span className="text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full bg-amber-50 text-amber-700" data-testid={`mq-role-removed-${c.contact_id}`}>
                  Removed from GHL
                </span>
              )}
              {detached && c.contact_id === item.match_contact_id && (
                <span className="text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-600" data-testid={`mq-role-match-${c.contact_id}`}>
                  Possible match
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-1 space-y-0.5">
              {c.emails?.[0]?.value && <div className="flex items-center gap-1"><Mail size={12} />{c.emails[0].value}</div>}
              {c.phones?.[0]?.value && <div className="flex items-center gap-1"><Phone size={12} />{formatPhone(c.phones[0].value)}</div>}
              <div>
                {label(CONTACT_TYPES, c.contact_type)}
                {c.lead_status ? ` · ${label(LEAD_STATUSES, c.lead_status)}` : ''}
              </div>
              <div>Created {dateStr(c.created_at)}</div>
              {c.ghl_edit_url && (
                <a href={c.ghl_edit_url} target="_blank" rel="noreferrer" className="underline inline-block" style={{ color: '#070B5F' }}>
                  Edit in GHL
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {resolved ? (
        <div className="mt-4 flex items-center gap-2">
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: pill.bg, color: pill.color }}>
            {pill.label}
          </span>
          <span className="text-xs text-slate-400">{dateStr(item.resolved_at)}</span>
        </div>
      ) : (
        <div className="mt-4 flex items-center gap-2">
          {item.kind === 'ghl_duplicate' ? (
            <p className="text-xs text-slate-500 flex-1">
              Both records are managed in GoHighLevel. Merge them in GHL; BFY follows on the next sync.
            </p>
          ) : !isPair ? (
            <p className="text-xs text-slate-500 flex-1">Pick the pair on each profile page.</p>
          ) : (
            <button
              className={detached && item.suggested_action !== 'merge' ? 'btn-outline' : 'btn-gold'}
              disabled={busy}
              onClick={onMerge}
              data-testid={`mq-merge-${item.item_id}`}
            >
              {busy && <Loader2 className="animate-spin" size={14} />} {detached ? 'Merge into match' : 'Merge'}
            </button>
          )}
          {detached ? (
            <button
              className={item.suggested_action === 'archive' ? 'btn-gold' : 'btn-outline'}
              disabled={busy}
              onClick={onArchive}
              data-testid={`mq-archive-${item.item_id}`}
            >
              Archive
            </button>
          ) : (
            <button className="btn-outline" disabled={busy} onClick={onDismiss} data-testid={`mq-dismiss-${item.item_id}`}>
              Keep separate
            </button>
          )}
          {detached && item.suggested_action && (
            <span className="text-xs text-slate-400" data-testid={`mq-suggested-${item.item_id}`}>
              Suggested: {item.suggested_action === 'merge' ? 'merge into match' : 'archive'}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function MergeQueuePage() {
  const { user } = useAuth();
  const canMerge = user?.is_super_admin || (user?.permissions || []).includes('contacts.merge');
  const [kindFilter, setKindFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('open'); // 'open' | 'resolved'
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [survivorChoice, setSurvivorChoice] = useState({}); // item_id -> contact_id
  const [busy, setBusy] = useState({}); // item_id -> bool

  const latest = useRef(0); // newest request wins; a slow older reply must not overwrite it

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!canMerge) { setLoading(false); return; }
    const req = ++latest.current;
    if (!quiet) setLoading(true);
    try {
      // "resolved" (merged + dismissed together) is one server query, so one total to page.
      const params = { status: statusFilter, limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (kindFilter) params.kind = kindFilter;
      const doc = await mergeQueue.list(params);
      if (req !== latest.current) return;
      const rows = doc.items || [];
      const count = doc.total ?? rows.length;
      // The queue shrank under us (a merge elsewhere): step back to the last page that exists.
      if (rows.length === 0 && count > 0 && page > 0) {
        setPage(Math.ceil(count / PAGE_SIZE) - 1);
        return;
      }
      setItems(rows);
      setTotal(count);
      setSurvivorChoice((prev) => {
        const defaults = {};
        for (const it of rows) if (it.suggested_survivor_id) defaults[it.item_id] = it.suggested_survivor_id;
        return { ...defaults, ...prev };
      });
    } catch (e) {
      if (req === latest.current) toast({ title: 'Failed to load', description: errorMessage(e) });
    } finally {
      if (req === latest.current) setLoading(false);
    }
  }, [canMerge, kindFilter, statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  // After a merge or dismiss the server decides what the page holds: a merge can
  // close other items (superseded pairs) and slide the next page's rows up. The
  // ref keeps the reload on the page the reader is on now, not the one they clicked on.
  const loadRef = useRef(load);
  loadRef.current = load;
  const reload = () => loadRef.current({ quiet: true });

  const changeFilter = (setter) => (value) => { setter(value); setPage(0); };

  const handleMerge = async (item) => {
    const survivorId = survivorChoice[item.item_id] || item.suggested_survivor_id;
    if (!survivorId) return;
    const ok = await confirmDialog({
      title: 'Merge these two contacts?',
      description: 'The other record is retired and everything on it moves to the one you keep. This cannot be undone.',
      confirmText: 'Merge',
    });
    if (!ok) return;
    setBusy((b) => ({ ...b, [item.item_id]: true }));
    try {
      await mergeQueue.merge(item.item_id, survivorId);
      toast({ title: 'Merged' });
      await reload();
    } catch (e) {
      toast({ title: 'Merge failed', description: errorMessage(e) });
    } finally {
      setBusy((b) => ({ ...b, [item.item_id]: false }));
    }
  };

  const handleDismiss = async (item) => {
    setBusy((b) => ({ ...b, [item.item_id]: true }));
    try {
      await mergeQueue.dismiss(item.item_id);
      toast({ title: 'Kept separate' });
      await reload();
    } catch (e) {
      toast({ title: 'Failed', description: errorMessage(e) });
    } finally {
      setBusy((b) => ({ ...b, [item.item_id]: false }));
    }
  };

  const handleArchive = async (item) => {
    const ok = await confirmDialog({
      title: 'Archive this contact?',
      description: 'It was removed from GoHighLevel. Archiving hides it from your contacts; its history and documents are kept.',
      confirmText: 'Archive',
    });
    if (!ok) return;
    setBusy((b) => ({ ...b, [item.item_id]: true }));
    try {
      await mergeQueue.archive(item.item_id);
      toast({ title: 'Archived' });
      await reload();
    } catch (e) {
      toast({ title: 'Archive failed', description: errorMessage(e) });
    } finally {
      setBusy((b) => ({ ...b, [item.item_id]: false }));
    }
  };

  if (!canMerge) {
    return (
      <div className="p-4 md:p-8" data-testid="merge-queue-page">
        <p className="text-slate-500" data-testid="merge-queue-no-permission">You need the contacts.merge permission</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="merge-queue-page">
      <div>
        <Link to="/portal/contacts" className="text-sm text-slate-500 inline-flex items-center gap-1 hover:text-[#070B5F]" data-testid="merge-queue-back">
          <ArrowLeft size={14} /> Back to contacts
        </Link>
        <h1 className="font-serif text-3xl font-bold mt-2" style={{ color: '#070B5F' }}>Needs review</h1>
        <p className="text-slate-500 mt-1">GoHighLevel matches that need a human decision</p>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {KIND_FILTERS.map((f) => (
          <button
            key={f.k || 'all'}
            onClick={() => changeFilter(setKindFilter)(f.k)}
            className={kindFilter === f.k ? 'btn-gold' : 'btn-outline'}
            data-testid={`mq-kind-${f.k || 'all'}`}
          >
            {f.l}
          </button>
        ))}
        <span className="mx-2 text-slate-300">|</span>
        {['open', 'resolved'].map((s) => (
          <button
            key={s}
            onClick={() => changeFilter(setStatusFilter)(s)}
            className={statusFilter === s ? 'btn-gold' : 'btn-outline'}
            data-testid={`mq-status-${s}`}
          >
            {s === 'open' ? 'Open' : 'Resolved'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-16 flex items-center justify-center text-slate-400">
          <Loader2 className="animate-spin mr-2" size={20} /> Loading...
        </div>
      ) : items.length === 0 ? (
        <div className="p-16 text-center text-slate-500" data-testid="merge-queue-empty">Nothing to review.</div>
      ) : (
        <div className="space-y-4">
          {items.map((item) => (
            <MergeQueueItem
              key={item.item_id}
              item={item}
              survivorId={survivorChoice[item.item_id]}
              onChooseSurvivor={(cid) => setSurvivorChoice((prev) => ({ ...prev, [item.item_id]: cid }))}
              busy={!!busy[item.item_id]}
              onMerge={() => handleMerge(item)}
              onDismiss={() => handleDismiss(item)}
              onArchive={() => handleArchive(item)}
            />
          ))}
        </div>
      )}

      {!loading && total > 0 && items.length > 0 && (
        <div className="flex items-center justify-between text-sm text-slate-500" data-testid="mq-pager">
          <span data-testid="mq-range">
            Showing {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + items.length} of {total}
          </span>
          <div className="flex gap-2">
            <button className="btn-outline disabled:opacity-40" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="mq-prev">
              Previous
            </button>
            <button className="btn-outline disabled:opacity-40" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage((p) => p + 1)} data-testid="mq-next">
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
